import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../../features/setting/setting.slice.ts'
import { convertPxToNumber } from '../../../../utils/helpers.ts'
import {
  useListFirmOptionsQuery,
  useListGroupOptionsQuery,
} from '../../../../apis/option/option-api.api.ts'
import {
  AccountFirmRole,
  AccountInternalRole,
  AccountRegion,
  AccountSubRegion,
  AccountType,
} from '../../../../enums/management-input.enum.ts'
import Col from 'antd/es/grid/col'
import Row from 'antd/es/grid/row'
import Form from 'antd/es/form/index'
import Select from 'antd/es/select/index'
import Spin from 'antd/es/spin/index'
import { UserManagementFormFieldTypes } from './user-management-form.tsx'
import { FormInstance } from 'antd/lib/index'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { GROUP_ROLE } from '../../../../router/roles.ts'

interface UserManagementNormalFormProps {
  form: FormInstance<UserManagementFormFieldTypes>
}

export const UserManagementNormalForm: React.FC<
  UserManagementNormalFormProps
> = ({ form }) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const accountType = Form.useWatch('accountType', form)
  const internalRole = Form.useWatch('internalRole', form)
  const firmId = Form.useWatch('firmId', form)
  const groupRole = Form.useWatch('groupRole', form)
  const isContributor = groupRole === GROUP_ROLE.ROLE_CONTRIBUTOR
  const isCaptain = groupRole === GROUP_ROLE.ROLE_CAPTAIN

  const { currentData: firmOptionData, isLoading: isLoadingFirmOptions } =
    useListFirmOptionsQuery()

  const { currentData: groupOptionData } = useListGroupOptionsQuery(
    firmId ?? '',
    { skip: !firmId }
  )

  const filteredGroupOptions = useMemo(() => {
    if (!groupOptionData?.data) return []

    if (groupRole !== GROUP_ROLE.ROLE_MANAGER) {
      return groupOptionData.data.filter((option) => option.label !== 'All')
    }

    return groupOptionData.data
  }, [groupOptionData?.data, groupRole])

  const firmRoleOptions = [
    {
      value: AccountFirmRole.Normal,
      label: AccountFirmRole.Normal,
    },
    {
      value: AccountFirmRole.Compliance,
      label: AccountFirmRole.Compliance,
    },
  ]

  const groupRoleOptions = [
    {
      value: GROUP_ROLE.ROLE_CAPTAIN,
      label: GROUP_ROLE.ROLE_CAPTAIN,
    },
    {
      value: GROUP_ROLE.ROLE_MANAGER,
      label: GROUP_ROLE.ROLE_MANAGER,
    },
    {
      value: GROUP_ROLE.ROLE_CONTRIBUTOR,
      label: GROUP_ROLE.ROLE_CONTRIBUTOR,
    },
  ]

  const subRegionOptions = [
    {
      value: AccountSubRegion.HknChina,
      label: AccountSubRegion.HknChina,
    },
    {
      value: AccountSubRegion.Taiwan,
      label: AccountSubRegion.Taiwan,
    },
  ]

  const firmRole = Form.useWatch('firmRole')
  const groupId = Form.useWatch('groupId')
  const region =
    filteredGroupOptions?.find((option) => option.value === groupId)?.type ?? ''

  return (
    <>
      {(accountType === AccountType.External ||
        (accountType === AccountType.Internal &&
          internalRole === AccountInternalRole.Normal)) && (
        <Row
          gutter={[
            convertPxToNumber(themeStyle.marginBase),
            convertPxToNumber(themeStyle.marginBase),
          ]}
        >
          <Col span={12}>
            {isLoadingFirmOptions ? (
              <Spin />
            ) : (
              <Form.Item
                label={t('user_management.user_modal.form.firm')}
                name={'firmId'}
                rules={[
                  {
                    required: true,
                    message: t(
                      'user_management.user_modal.form.please_select_firm'
                    ),
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder={t('user_management.user_modal.form.select_firm')}
                  options={firmOptionData?.data
                    ?.filter((firm) =>
                      accountType === AccountType.External
                        ? firm.type === 'External'
                        : firm.type === 'Internal'
                    )
                    .map((firm) => ({
                      value: firm.value,
                      label: firm.label,
                    }))}
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={() => {
                    form.resetFields(['groupRole', 'groupId'])
                  }}
                />
              </Form.Item>
            )}
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('user_management.user_modal.form.firm_role')}
              name={'firmRole'}
              rules={[
                {
                  required: true,
                  message: t(
                    'user_management.user_modal.form.please_select_firm_role'
                  ),
                },
              ]}
              shouldUpdate
            >
              <Select
                placeholder={t(
                  'user_management.user_modal.form.select_firm_role'
                )}
                options={firmRoleOptions}
              />
            </Form.Item>
          </Col>
        </Row>
      )}
      {firmRole === AccountFirmRole.Normal && (
        <Row gutter={[convertPxToNumber(themeStyle.marginBase), 0]}>
          <Col span={12}>
            <Form.Item
              label={t('user_management.user_modal.form.group_role')}
              name={'groupRole'}
              rules={[
                {
                  required: true,
                  message: t(
                    'user_management.user_modal.form.please_select_group_role'
                  ),
                },
              ]}
            >
              <Select
                placeholder={t(
                  'user_management.user_modal.form.select_group_role'
                )}
                onChange={() => {
                  form.resetFields(['groupId', 'subRegion'])
                }}
                options={groupRoleOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('user_management.user_modal.form.group')}
              name={'groupId'}
              rules={[
                {
                  required: true,
                  message: t(
                    'user_management.user_modal.form.please_select_group'
                  ),
                },
              ]}
              help={
                !firmId
                  ? t(
                      'user_management.user_modal.form.please_select_firm_first'
                    )
                  : undefined
              }
            >
              <Select
                placeholder={t('user_management.user_modal.form.select_group')}
                disabled={!firmId}
                options={filteredGroupOptions}
                onChange={(value) => {
                  const selectedRegion =
                    filteredGroupOptions?.find(
                      (option) => option.value === value
                    )?.type ?? ''

                  if (!selectedRegion.includes(AccountRegion.GreaterChina)) {
                    form.resetFields(['subRegion'])
                  }
                }}
              />
            </Form.Item>
          </Col>
          {(isContributor || isCaptain) &&
            region.includes(AccountRegion.GreaterChina) && (
              <Col span={12}>
                <Form.Item
                  label={t('user_management.user_modal.form.sub_region')}
                  name={'subRegion'}
                  rules={[
                    {
                      required: true,
                      message: t(
                        'user_management.user_modal.form.please_select_sub_region'
                      ),
                    },
                  ]}
                  help={
                    !groupId
                      ? t(
                          'user_management.user_modal.form.please_select_group_first'
                        )
                      : undefined
                  }
                >
                  <Select
                    placeholder={t(
                      'user_management.user_modal.form.select_sub_region'
                    )}
                    options={subRegionOptions}
                  />
                </Form.Item>
              </Col>
            )}
          <Col span={12}></Col>
        </Row>
      )}
    </>
  )
}
